export type ViewportBoundingBox = {
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
};

export type CriticalBoundingBoxByScreen = {
  mobilePortrait: ViewportBoundingBox;
  mobileLandscape: ViewportBoundingBox;
  tabletPortrait: ViewportBoundingBox;
  tabletLandscape: ViewportBoundingBox;
  desktop: ViewportBoundingBox;
};

export type VideoFrameStyle = {
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
  objectPositionXPct: number;
  objectPositionYPct: number;
};

export type VideoFrameStyleByScreen = {
  mobilePortrait?: VideoFrameStyle;
  mobileLandscape?: VideoFrameStyle;
  tabletPortrait?: VideoFrameStyle;
  tabletLandscape?: VideoFrameStyle;
  desktop?: VideoFrameStyle;
};

export type ViewportSizeReference = {
  widthPx: number;
  heightPx: number;
};

export type CameraVideoViewportSettings = {
  criticalBoundingBoxByScreen: CriticalBoundingBoxByScreen;
  manualFrameReferenceViewport: ViewportSizeReference;
  manualFrameStyleByScreen: VideoFrameStyleByScreen;
};

type TeleopSettings = {
  joystick: {
    maxDist: number;
    keyRepeatMs: number;
    motionSendIntervalMs: number;
    motionHorizonMs: number;
    motionBufferedAmountLimitBytes: number;
  };
  rotation: {
    keyboardVelocity: number;
  };
  camera: {
    defaultTrack: string;
    secondaryTrack: string;
    swipeThreshold: number;
    swipeRatio: number;
    wheelThreshold: number;
    swipeCooldownMs: number;
  };
  video: {
    source: {
      widthPx: number;
      heightPx: number;
    };
    viewport: {
      mobileMaxWidthPx: number;
      tabletMaxWidthPx: number;
      cameraProfiles: {
        overhead: CameraVideoViewportSettings;
        perspective: CameraVideoViewportSettings;
      };
    };
  };
  attempts: {
    maxAttempts: number;
    countdownSeconds: number;
    countdownGoMs: number;
    bonusPromptTimeoutMs: number;
    attemptStartHandoffTimeoutMs: number;
    attemptWaitEnqueuedReason: string;
    resultResolveTimeoutMs: number;
  };
};

export const TELEOP_SETTINGS: TeleopSettings = {
  joystick: {
    maxDist: 70,
    keyRepeatMs: 50,
    motionSendIntervalMs: 50,
    motionHorizonMs: 200,
    motionBufferedAmountLimitBytes: 1024,
  },
  rotation: {
    keyboardVelocity: 0.35,
  },
  camera: {
    defaultTrack: 'robot_overhead',
    secondaryTrack: 'robot_perspective',
    swipeThreshold: 70,
    swipeRatio: 1.3,
    wheelThreshold: 36,
    swipeCooldownMs: 420,
  },
  video: {
    source: {
      widthPx: 1280,
      heightPx: 720,
    },
    viewport: {
      mobileMaxWidthPx: 600,
      tabletMaxWidthPx: 1024,
      cameraProfiles: {
        overhead: {
          criticalBoundingBoxByScreen: {
            mobilePortrait: {
              leftPct: 0,
              topPct: 0,
              widthPct: 30,
              heightPct: 50,
            },
            mobileLandscape: {
              leftPct: 34,
              topPct: 0,
              widthPct: 36,
              heightPct: 74,
            },
            tabletPortrait: {
              leftPct: 34,
              topPct: 0,
              widthPct: 36,
              heightPct: 64,
            },
            tabletLandscape: {
              leftPct: 34,
              topPct: 0,
              widthPct: 36,
              heightPct: 64,
            },
            desktop: {
              leftPct: 0,
              topPct: 0,
              widthPct: 100,
              heightPct: 100,
            },
          },
          manualFrameReferenceViewport: {
            widthPx: 375,
            heightPx: 812,
          },
          manualFrameStyleByScreen: {
            mobilePortrait: {
              leftPct: 0,
              topPct: 15,
              widthPct: 100,
              heightPct: 60,
              objectPositionXPct: 50,
              objectPositionYPct: 20,
            },
            desktop: {
              leftPct: 0,
              topPct: 0,
              widthPct: 100,
              heightPct: 100,
              objectPositionXPct: 50,
              objectPositionYPct: 50,
            },
          },
        },
        perspective: {
          criticalBoundingBoxByScreen: {
            mobilePortrait: {
              leftPct: 22,
              topPct: 15,
              widthPct: 70,
              heightPct: 100,
            },
            mobileLandscape: {
              leftPct: 15,
              topPct: 0,
              widthPct: 70,
              heightPct: 100,            },
            tabletPortrait: {
              leftPct: 15,
              topPct: 0,
              widthPct: 70,
              heightPct: 100,            },
            tabletLandscape: {
              leftPct: 15,
              topPct: 0,
              widthPct: 70,
              heightPct: 100,            },
            desktop: {
              leftPct: 0,
              topPct: 0,
              widthPct: 100,
              heightPct: 100,
            },
          },
          manualFrameReferenceViewport: {
            widthPx: 375,
            heightPx: 812,
          },
          manualFrameStyleByScreen: {
            mobilePortrait: {
              leftPct: 0,
              topPct: 18,
              widthPct: 100,
              heightPct: 64,
              objectPositionXPct: 50,
              objectPositionYPct: 30,
            },
            mobileLandscape: {
              leftPct: 0,
              topPct: 0,
              widthPct: 100,
              heightPct: 100,
              objectPositionXPct: 50,
              objectPositionYPct: 50,
            },
            tabletLandscape: {
              leftPct: 0,
              topPct: 0,
              widthPct: 100,
              heightPct: 100,
              objectPositionXPct: 50,
              objectPositionYPct: 50,
            },
            desktop: {
              leftPct: 0,
              topPct: 0,
              widthPct: 100,
              heightPct: 100,
              objectPositionXPct: 50,
              objectPositionYPct: 50,
            },
          },
        },
      },
    },
  },
  attempts: {
    maxAttempts: 3,
    countdownSeconds: 3,
    countdownGoMs: 700,
    bonusPromptTimeoutMs: 10_000,
    attemptStartHandoffTimeoutMs: 6_000,
    attemptWaitEnqueuedReason: 'attempt_start_enqueued',
    resultResolveTimeoutMs: 30_000,
  },
};
